import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import QuoteBuilderForm from 'components/layout_and_quote_builder/quote_builder/QuoteBuilderForm.jsx';
import LayoutNavSectionList from 'components/layout_and_quote_builder/LayoutNavSectionList.jsx';
import MyEventListener from 'components/common/MyEventListener.jsx';
import PostRequest from 'components/common/PostRequest.jsx';
import {
  buildSetupWizardStages, shownStageIndex, MACHINE_THICKNESS_MISMATCH,
} from 'components/frank/utils/setupWizardStages.js';
import { linkedSectionIndexes } from 'components/frank/utils/linkedNavSection.js';
import { machineCount, sectionMachine, refreshCountsAndResolve } from 'components/frank/utils/machineSettings.js';
import {
  MaterialPriceStep, MachineEntryStep, MachinePriceStep, MachineThicknessMismatchStep,
} from 'components/frank/SetupStepForms.jsx';
import StandardManufacturingLinesToggle from 'components/frank/StandardManufacturingLinesToggle.jsx';
import { setupStepSurfaceProps } from 'components/frank/utils/setupWizardLayout.js';
import { SectionDisabledManufacturingLines } from 'components/frank/DisabledManufacturingLines.jsx';
import { WIZARD_CARDS_PER_ROW } from 'components/frank/ManufacturingLineLists.jsx';

// The guided setup wizard for the /mcc (Frank) edit page -- a modal that hand-holds the user through
// configuring a quote, one decision per stage, so it is always obvious what to pick next:
//
//   Stage 1  Quantity in order (default 1000)
//   then, per priceable SECTION (e.g. Carton -- NOT per sub_section: one line + one solution per section):
//     pick a manufacturing line   (the filter panel + the manufacturing-line listing; a pick just MARKS it)
//     pick a solution             (the picked plan's solution cards; a click just MARKS one) -> Done on the last
//
// It REUSES the page's own per-section flow (ManufacturingLineLists, via LayoutNavSectionList) rather than
// rebuilding it: each section stage renders that component with wizardStage set, so all of its fetching,
// scoring and solution grouping is shared. The wizard only adds the chrome (stage title, "Stage X of N", a
// progress bar that fills as you advance, Back / Next / Done) and does the select-solution POST when it leaves
// a solution stage -- the same POST SelectedSolutionSections does on the normal page. Because that POST updates
// window.user_quote_nav_sections, closing the wizard reveals the normally-rendered, now-priced page -- and
// onClose reports the section it priced, so the page opens on that section's Pricing tab.
const FrankSetupWizard = ({
  navSections, config, onClose, manufacturingLinesSource, onManufacturingLinesSourceChange,
  onUseStandardLineForSection, machinesRevision
}) => {
  // Quantity in order, kept in step with the count field the quantity stage renders (it publishes
  // quote_builder.count_changed on change), so the solution cards in the section stages price against the
  // current quantity. Seeded from the quote, falling back to 1000 (the create default). The count_changed
  // listener is registered further down, once the per-section marks it resets are declared.
  const [count, setCount] = useState((window.user_quote && window.user_quote.count) || 1000);

  // The stages, from the step manager: quantity, then per choosing nav section its MISSING own-lines setup steps
  // (material price, each machine and its price) followed by its line and solution. A linked section inherits its
  // line and solution from another, so the wizard never asks about it. Recomputed EVERY render from the live
  // setup state -- the server-seeded per-section requirements plus the live own counts -- so as a setup step is
  // completed it drops out and "Step X of N" shrinks to what is still left. On Standard lines there are no setup
  // steps (the example account has everything). See setupWizardStages and SectionSetupRequirements.
  const stages = buildSetupWizardStages(navSections, {
    linkedSectionIndexes: linkedSectionIndexes(window.user_quote_nav_sections),
    source: manufacturingLinesSource,
    requirementsBySection: window.frank_section_setup_requirements || {},
    hasMaterialPrice: (key) => machineCount(key) > 0,   // a material is a UserUnitPrice; its count is price-based
    // Per section, counting only the owner's machines for the section's part_type (Frank::OwnSetupCounts'
    // machines_by_section) -- so a hard cardboard owner with only a carton die-cutter is walked through adding one,
    // and an owner with an unpriced one is led to price THAT machine.
    sectionMachine,
    includeManufacturingLinesSource: Boolean(window.frank_has_priced_user_quote),
  });
  const totalStages = stages.length;

  const [currentStage, setCurrentStage] = useState(0);
  // Pressing Done on the LAST stage when that stage is the linked corrugated material step. Unlike every other setup
  // step (each followed by its line and solution), this one is the tail, so completing it must CLOSE the wizard
  // rather than clamp the user back onto the already-answered lamination solution. Set on that Done, honored by the
  // clamp/close effect (below, after closeWizard) once the save re-solves and the step drops. See
  // setupWizardStages' trailing corrugated material step.
  const closeOnTrailingStepDoneRef = useRef(false);
  const stageIndex = Math.min(currentStage, totalStages - 1);
  // A price step that names no machine is never shown: it is reachable only when the stage list shifted under the
  // wizard, and its row's "Add a machine" step is what is still to do (shownStageIndex). Moving the index there --
  // rather than drawing that step in place -- keeps the wizard counting from the step it shows, so once the machine
  // is added and that step drops, the same index lands on pricing it.
  const shownIndex = shownStageIndex(stages, stageIndex);
  useEffect(() => {
    if (shownIndex !== stageIndex) { setCurrentStage(shownIndex); }
  }, [shownIndex, stageIndex]);
  // A machine-price step's form shows no buttons of its own -- it hands its submit up here, and the wizard's Next
  // saves it (see the footer). savingSetupStep disables Next between the click and the save landing; it clears
  // when the step changes (a successful save advances the wizard) or when the save reports an error.
  const setupFormSubmitRef = useRef(null);
  const [savingSetupStep, setSavingSetupStep] = useState(false);
  // The material step opens on the price LIST and only becomes a save-with-Next step once its form leaves that
  // list for a create/copy/edit sub-form. The form reports its sub-state up here ('list' vs new/copy/edit), so
  // the footer knows whether to show Next (form open) or leave it a plain picker (list). Its cancel is handed up
  // too, so the wizard's Back returns to the list instead of leaving the stage. Reset to 'list' on every stage
  // change (below), so a freshly entered material stage never inherits the previous form's open state.
  const [materialFormState, setMaterialFormState] = useState('list');
  const materialFormCancelRef = useRef(null);
  // Take only the form OPENING from the form (a create/copy/edit sub-form). The CLOSE back to 'list' is driven by
  // the wizard itself -- goBack and the stage-change reset -- so the transient STATE_LIST the copy-from-system
  // re-seed briefly flashes (QuoteBuilderForm) can never collapse the footer to the picker mid-open.
  const handleMaterialFormState = useCallback((state) => {
    if (state !== 'list') { setMaterialFormState(state); }
  }, []);
  // Bumped when a setup step completes, to force the wizard to recompute its own stages. refreshCountsAndResolve
  // re-solves the whole page too (frank:own-machines-changed -> machinesRevision), but that page trigger can be
  // swallowed if a speculative fingerprint refresh is already in flight (FrankQuoteBuilderPage) -- so the wizard
  // does not depend on it: this local bump ALWAYS recomputes the steps and clears the Next spinner once the
  // fresh counts are in, whether or not the page re-solved.
  const [setupRevision, setSetupRevision] = useState(0);
  const resolveSetup = () => { refreshCountsAndResolve().finally(() => setSetupRevision((revision) => revision + 1)); };
  // The marked plan / marked solution PER SECTION, filed by section index. Reported up from the mounted
  // ManufacturingLineLists via the callbacks below; read back to enable Next / Done and to know what to POST.
  const [pickedBySection, setPickedBySection] = useState({});
  const [selectionBySection, setSelectionBySection] = useState({});
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState(false);

  // The FIRST section the wizard priced -- persisted a solution for -- or null while it has priced none. Handed
  // to onClose so the page opens THAT (the first nav-section's) Pricing tab: the wizard walks the sections in
  // order pricing each, so the first one priced is the first nav-section, and closing lands the user at the top
  // of the priced quote rather than deep on whichever section happened to be finished last. It also spares them
  // the manufacturing-line listing the page would otherwise still be sitting on -- a choice they have made.
  // Set once per run (kept at the first index) and reset to null whenever the priced sections are cleared -- an
  // order-count change, a source switch, or borrowing a standard line. Reported on every close, the "Done" and
  // the "×" alike, since a user
  // who set up one section and dismissed the wizard has priced that section just the same.
  // A ref, not state: nothing renders from it, and the close handlers must read what the last persist wrote.
  const pricedSectionIndexRef = useRef(null);

  const stage = stages[stageIndex];
  const sectionIndex = stage.sectionIndex;
  // The stages only ever host the CURRENT section's component, so the picked/selection callbacks fire for the
  // current section. A ref carries that index into the (stable) callbacks, so they never re-create -- a
  // re-created callback would re-fire ManufacturingLineLists' notify effect every render.
  const sectionIndexRef = useRef(sectionIndex);
  sectionIndexRef.current = sectionIndex;

  const handlePicked = useCallback((identifier) => {
    setPickedBySection((previous) => ({ ...previous, [sectionIndexRef.current]: identifier }));
  }, []);
  const handleSelection = useCallback((group) => {
    setSelectionBySection((previous) => ({ ...previous, [sectionIndexRef.current]: group }));
  }, []);
  // The wizard-only machine PRICE a found line needs (its machine has no price for this section), reported up per
  // section by the line stage's ManufacturingLineLists. Set -> the line stage runs the SAME footer-integrated price
  // form the machine_price step uses (the footer's Next saves it -- not a second Save button); null -> the listing is
  // quotable and shows its cards. The report only fires when the answer changes, so this never loops.
  const [machinePriceNeededBySection, setMachinePriceNeededBySection] = useState({});
  const handleWizardMachinePrice = useCallback((machine) => {
    setMachinePriceNeededBySection((previous) => ({ ...previous, [sectionIndexRef.current]: machine || null }));
  }, []);

  // The count field lives on the wizard's quantity stage. A REAL order-count change forgets the wizard's OWN marked
  // line/solution and priced-section -- the same reset changeSource (below) does, for the same reason. The page's
  // own count listener clears the shared client + server state (FrankQuoteBuilderPage / the general-form persist).
  // Guarded against the no-op re-publish DelayedNumberFormField fires on mount with the seeded value, so opening
  // the wizard never wipes what is already marked.
  // Changing the currency on the quantity stage persists it the same way the /mcc page's Manage tab
  // (QuoteBuilderManagePanel) does: the general-form persist ignores currency, so POST set-currency, which saves the
  // quote's currency AND the per-user default. But UNLIKE the Manage panel it does NOT reload the page. The panel
  // reloads because currency is deliberately outside the response-cache fingerprint (Frank::ResponseCacheFingerprint),
  // so already-rendered priced content keeps its old formatting until the page is rebuilt -- but here there is nothing
  // rebuilt to fix: the wizard opens on a not-yet-priced quote, its later stages fetch their listings fresh (already
  // in the new currency), and a reload would only re-hydrate the SAME old-currency response cache (currency is not in
  // its key either), so it never corrected those; it just flickered the wizard back to this stage. The Manage panel is
  // not mounted while the wizard is open, so its reload cannot fire either. Guarded against a same-currency event and
  // against overlapping POSTs (re-armed once the save lands, since nothing reloads to do it for us), and window.user_quote
  // is updated in place so the no-op guard reads the current currency for the next change.
  const currencyChangeInFlight = useRef(false);
  MyEventListener([
    { key: 'quote_builder.count_changed', callback: (message) => {
      const nextCount = Number(message.detail);
      if (Number.isFinite(nextCount) && nextCount > 0 && nextCount !== Number(count)) {
        setPickedBySection({});
        setSelectionBySection({});
        setMachinePriceNeededBySection({});
        pricedSectionIndexRef.current = null;
      }
      setCount(() => message.detail);
    } },
    { key: 'quote_builder.currency_changed', callback: (message) => {
      const nextCurrency = message.detail;
      if (currencyChangeInFlight.current) { return; }
      if (!nextCurrency || nextCurrency === (window.user_quote && window.user_quote.currency)) { return; }
      currencyChangeInFlight.current = true;
      PostRequest({
        url: `/quote-builder/${window.drawing_id}/${window.drawing_slug}/${window.user_quote.id}/set-currency/${nextCurrency}`,
        data: {},
        callback: () => {
          if (window.user_quote) { window.user_quote.currency = nextCurrency; }
          currencyChangeInFlight.current = false;
        }
      });
    } }
  ]);

  // Switching the manufacturing-lines source re-solves every section against a DIFFERENT set of machines, so the
  // manufacturing line and solution the user marked against the old source cannot be made on the new one -- left
  // as they are, the wizard sits on the Solution stage over a plan the new source cannot produce ("This plan
  // produces no solutions"). Forget every section's marked plan and solution on a switch, and walk the wizard back
  // to the stage where the section it was on is chosen afresh. The section is RECORDED here rather than read in the
  // effect below, because the stage list is rebuilt from the new source there and the old stage index no longer
  // names the same stage. The page's onManufacturingLinesSourceChange does the rest (persist + clear the address
  // bar and saved solutions the page re-seeds from).
  const pendingSourceResetSectionRef = useRef(null);
  const changeSource = (next) => {
    if (next === manufacturingLinesSource) { return; }
    // A solution POST (goNext -> persistSolution) or a setup-step save is in flight. Its callback lands against the
    // OLD source -- persistSolution overwrites window.user_quote_nav_sections with an old-source solution, announces
    // it, and goNext then steps the stage -- which would undo the reset below and put the wizard right back on the
    // stale plan. Ignore the switch until the in-flight write settles (the same writes that disable Back).
    if (posting || savingSetupStep) { return; }
    // Switching source leaves the current step, so disarm the "finishing the trailing corrugated step closes the
    // wizard" intent (as goBack does) -- a later unrelated tail-shrink must not close the wizard.
    closeOnTrailingStepDoneRef.current = false;
    // The source stage belongs to no section, and choosing the source is its whole question, so it stays put
    // rather than snapping (an undefined section would match the quantity stage).
    if (stage.type !== 'manufacturing_lines_source') {
      pendingSourceResetSectionRef.current = sectionIndexRef.current;
    }
    setPickedBySection({});
    setSelectionBySection({});
    setMachinePriceNeededBySection({});
    // Every section's saved solution is cleared by the switch, so nothing is priced any more: forget the section
    // the wizard would otherwise ask the page to open on the Pricing tab (it is now unpriced, so that tab is shut).
    pricedSectionIndexRef.current = null;
    onManufacturingLinesSourceChange(next);
  };
  // Once the new source's stages are in, snap back to the recorded section's FIRST stage -- its first still-missing
  // own-lines setup step, or its manufacturing-line stage when nothing is missing. Before paint (useLayoutEffect),
  // so the invalidated Solution stage is never shown. Gated on a pending switch, so an unrelated re-render (and the
  // initial mount) never moves the user.
  useLayoutEffect(() => {
    if (pendingSourceResetSectionRef.current === null) { return; }
    const section = pendingSourceResetSectionRef.current;
    pendingSourceResetSectionRef.current = null;
    const firstStageOfSection = stages.findIndex((each) => each.sectionIndex === section);
    // The recorded section may have NO stage on the new source: the linked corrugated section contributes only its
    // material step, which own lines add and Standard does not, so switching to Standard from that tail step leaves
    // it stageless. Fall back to the first section stage (the lamination it follows) rather than the Quantity stage,
    // so the switch lands the user back in the flow to re-choose on the new source, not at the very start.
    const firstSectionStage = stages.findIndex((each) => each.sectionIndex != null);
    setCurrentStage(firstStageOfSection >= 0 ? firstStageOfSection : Math.max(firstSectionStage, 0));
  }, [manufacturingLinesSource]);

  // BORROW A STANDARD LINE when the owner's OWN lines came up empty. The standard-lines fallback shown under the
  // "no manufacturing lines match" finding (StandardManufacturingLinesFallback, own lines only) reports the line
  // the owner picked. Like a source switch this forgets every section's marked plan and solution -- but it KEEPS
  // this section on the borrowed line and lands on its SOLUTION stage, so the owner carries on into solutions
  // rather than the empty listing. The page's onUseStandardLineForSection flips the whole quote to Standard, writes
  // the borrowed line into this section's address-bar state (which the remounted Solution stage re-seeds from) and
  // clears the rest; here we only mirror the pick locally and record where to land. Guarded like changeSource: an
  // in-flight solution POST or setup save lands against the OLD source and must settle first.
  const pendingStandardLineSectionRef = useRef(null);
  const handleUseStandardLine = (identifier) => {
    if (posting || savingSetupStep) { return; }
    // Borrowing a standard line leaves the current step, so disarm the trailing-step close intent (as goBack does).
    closeOnTrailingStepDoneRef.current = false;
    const section = sectionIndexRef.current;
    pendingStandardLineSectionRef.current = section;
    setPickedBySection({ [section]: identifier });
    setSelectionBySection({});
    setMachinePriceNeededBySection({});
    pricedSectionIndexRef.current = null;
    onUseStandardLineForSection(section, identifier);
  };
  // Once the new (Standard) source's stages are in, snap to the borrowed section's SOLUTION stage -- not its first
  // stage (the line is already picked). Before paint, so the intermediate line stage is never shown. Mutually
  // exclusive with the source-reset snap above: a borrow sets only THIS ref and a plain switch only the other, and
  // because the fallback that calls handleUseStandardLine shows on OWN lines only, a borrow always moves OWN ->
  // Standard -- so the source always changes and this ref is never left set for a later switch to pick up.
  useLayoutEffect(() => {
    if (pendingStandardLineSectionRef.current === null) { return; }
    const section = pendingStandardLineSectionRef.current;
    pendingStandardLineSectionRef.current = null;
    const solutionStage = stages.findIndex((each) => each.sectionIndex === section && each.type === 'solution');
    setCurrentStage(solutionStage >= 0 ? solutionStage : 0);
  }, [manufacturingLinesSource]);

  // The quantity stage renders the count field AND the currency picker, so the user sees the quote's currency and
  // can change it before pricing; it leaves out the rest of the general form (the windows question, the
  // exchange-rate table). count persists through the general section's own post-on-change wiring, kept by the
  // spread. Currency does NOT: the general-form endpoint ignores it (it is saved only by set-currency), so its
  // change is handled by the currency_changed listener below -- the same set-currency + reload the Manage panel does.
  const quantityConfig = {
    ...config.general,
    attributes: config.general.attributes.filter((attribute) => attribute.name === 'count' || attribute.name === 'currency'),
  };

  const isLastStage = stageIndex === totalStages - 1;
  // The material step has BOTH faces: on its price list it is a PICKER (choosing a material carries it forward),
  // but once its create/copy/edit sub-form is open it behaves like the machine-price step -- the wizard's Next
  // SAVES that form. materialFormState (reported up by the form) is what tells the two apart.
  const materialFormOpen = stage.type === 'material' && materialFormState !== 'list';
  // A PICKER step (pick a material off its list / add a machine) is left by choosing an item, which re-solves and
  // drops the step -- so it shows no Next, only the wizard's Back (which cancels the step). A price-form step
  // keeps Next: there Next SAVES the form (the form has no Save of its own), then the save re-solves and advances.
  const isPickerStep = (stage.type === 'material' && !materialFormOpen) || stage.type === 'machine_entry'
    || stage.type === MACHINE_THICKNESS_MISMATCH;
  // The line stage becomes a price-form step when its found line names an unpriced machine (reported up above): the
  // footer's Next saves the price form, exactly as a machine_price step's does, so the two share one save path.
  const lineStageMachinePrice = stage.type === 'line' ? (machinePriceNeededBySection[sectionIndex] || null) : null;
  const isPriceFormStep = stage.type === 'machine_price' || materialFormOpen || Boolean(lineStageMachinePrice);
  // Back is disabled on the first stage (always the quantity stage), while a solution POST is in flight (posting),
  // and while a setup-step save is in flight (savingSetupStep) -- Backing out mid-save would cancel the form while
  // its create POST could still land and advance the wizard. (A material stage is never the first stage, so its
  // "Back returns to the list" while the sub-form is open is always available.)
  const backDisabled = stageIndex === 0 || posting || savingSetupStep;

  const canAdvance = () => {
    if (posting) { return false; }
    if (stage.type === 'quantity' || stage.type === 'manufacturing_lines_source') { return true; }
    // A price-form step's Next saves the form; keep it live except while a save is already in flight. Checked
    // before the line branch so the line stage's price form (lineStageMachinePrice) saves rather than waiting on a
    // picked line. (Picker steps have no Next at all -- see the footer.)
    if (isPriceFormStep) { return !savingSetupStep; }
    if (stage.type === 'line') { return Boolean(pickedBySection[sectionIndex]); }
    if (stage.type === 'solution') { return Boolean(selectionBySection[sectionIndex]); }
    return false;
  };

  // Save the current price-form step through the submit its form handed up, guarding against a double click while
  // a save is already running. The save's afterSave (refreshCountsAndResolve) re-solves, which drops this step
  // and advances the wizard; a failed save clears the flag via onSaveError so Next comes back.
  const submitSetupPrice = () => {
    if (savingSetupStep || !setupFormSubmitRef.current) { return; }
    setSavingSetupStep(true);
    setupFormSubmitRef.current();
  };

  // Persist + price ONE section from its marked solution -- the same select-solution POST
  // SelectedSolutionSections does on the normal page (updates window.user_quote_nav_sections and announces it),
  // so when the wizard closes the page shows the priced section.
  const persistSolution = (sectionIdx, group) => new Promise((resolve, reject) => {
    PostRequest({
      url: `/mcc/${window.user_quote_id}/select-solution/${sectionIdx}`,
      data: { lists: group },
      callback: (data) => {
        window.user_quote_nav_sections = data.window_nav_sections;
        window.dispatchEvent(new CustomEvent('navSectionsUpdated', {
          detail: { windowNavSections: data.window_nav_sections, sourceIndex: sectionIdx }
        }));
        resolve(data);
      }
    }).catch(reject);
  });

  // Closing always reports which section the wizard priced (null if it priced none), so the page can follow the
  // user to that section's Pricing tab.
  const closeWizard = () => { onClose({ pricedSectionIndex: pricedSectionIndexRef.current }); };

  // Completing a setup step drops it, shrinking `stages` under the current index -- which is how the wizard advances
  // onto the next step. If the index ends up past the last stage (the tail shrank), pull it back -- UNLESS the tail
  // that shrank was the linked corrugated material step just finished with Done, in which case there is nothing left
  // to walk and the wizard closes (it would otherwise clamp back onto the already-answered lamination solution).
  useEffect(() => {
    if (currentStage <= totalStages - 1) { return; }
    if (closeOnTrailingStepDoneRef.current) {
      closeOnTrailingStepDoneRef.current = false;
      closeWizard();
      return;
    }
    setCurrentStage(Math.max(0, totalStages - 1));
  }, [totalStages, currentStage]);

  const goNext = async () => {
    if (!canAdvance()) { return; }
    setPostError(false);

    // On a price-form step, Next SAVES the form; the save re-solves and drops the step, which is what advances
    // the wizard -- so do not step the index here. On the LAST stage (the linked corrugated material step) there is
    // no next step to advance onto, so mark that finishing this save closes the wizard (see the clamp/close effect).
    if (isPriceFormStep) {
      if (isLastStage) { closeOnTrailingStepDoneRef.current = true; }
      submitSetupPrice();
      return;
    }

    // Leaving a solution stage prices that section before moving on (or closing).
    if (stage.type === 'solution') {
      setPosting(true);
      try {
        await persistSolution(sectionIndex, selectionBySection[sectionIndex]);
      } catch (error) {
        setPosting(false);
        setPostError(true);
        return; // keep the wizard open so the user can retry
      }
      // Keep the FIRST section priced, not the last: the wizard walks the sections in order, so the first one it
      // prices is the first nav-section, which is where the page opens on close (see the ref's comment above).
      if (pricedSectionIndexRef.current === null) { pricedSectionIndexRef.current = sectionIndex; }
      setPosting(false);
    }

    if (isLastStage) { closeWizard(); return; }
    setCurrentStage(stageIndex + 1);
  };

  const goBack = () => {
    if (backDisabled) { return; }
    // Leaving the trailing corrugated material step by hand cancels the "finishing it closes the wizard" intent a
    // failed-then-abandoned Done may have left armed, so a later unrelated tail-shrink can never close the wizard.
    closeOnTrailingStepDoneRef.current = false;
    setPostError(false);
    // With the material step's create/copy/edit sub-form open, Back returns to the frequently-used price list
    // (the form has no Cancel of its own) rather than leaving the stage. The wizard OWNS this 'list' transition
    // (the form's own 'list' reports are ignored -- see handleMaterialFormState), so it sets it here rather than
    // waiting on cancel()'s report; that also covers the rare window where the form is mid-remount and its
    // cancelRef is momentarily null, so Back still reverts the footer instead of doing nothing.
    if (materialFormOpen) {
      if (materialFormCancelRef.current) { materialFormCancelRef.current(); }
      setMaterialFormState('list');
      return;
    }
    setCurrentStage(stageIndex - 1);
  };

  // EVERY STAGE OPENS AT ITS OWN TOP. One scroll container (the body below) is reused by every stage, so the
  // place the reader had scrolled to was still there when the next stage drew into it: leaving the
  // manufacturing-line stage 840px down landed the solution cards 840px down as well, and Back landed on a
  // listing scrolled past its filters and its first row of cards. The wizard asks one question per stage --
  // each has to start where its answer starts.
  //
  // Nothing did this by accident either way: a next stage whose first frame was SHORTER than the body (the
  // solution stage while its solve was still out) came out at the top only because the browser clamps a scroll
  // position its content can no longer reach, and one whose cards were already in hand kept the old offset.
  //
  // The stage AND NOTHING ELSE is what re-runs this: the reader scrolls within a stage while picking, and a
  // reset on any other render would pull the list out from under that. Note ManufacturingLineLists' own
  // scroll-to-the-selection deliberately stands down in the wizard (#scrollToSelectedCard), so this is the only
  // hand on the modal's scroll position.
  //
  // Before paint (useLayoutEffect), so the new stage is drawn at the top rather than drawn where the last one
  // was left and then jumped.
  // Keyed on WHICH step is shown, not just the index: completing a setup step advances the wizard without the
  // index moving (the step drops from under it), so the body must still reopen at the top for the new step.
  const stageKey = `${stage.type}:${sectionIndex ?? ''}:${stage.machineKey ?? ''}`;
  const bodyRef = useRef(null);
  useLayoutEffect(() => {
    if (bodyRef.current) { bodyRef.current.scrollTop = 0; }
    // Every stage opens fresh: a new material stage starts on its price list, never inheriting the previous
    // material form's open sub-state (which lingers non-'list' until that form unmounts). Before paint, so the
    // footer is drawn as the plain picker rather than briefly showing Next over the new stage's loading form.
    setMaterialFormState('list');
  }, [stageKey]);
  // Clear an in-flight Next spinner on ANY of the ways a save resolves, so it can never hang:
  //   - the shown step changed (a landed save advanced the wizard; also how Back leaves a step) -- stageKey;
  //   - a setup step completed and the wizard recomputed -- setupRevision, bumped by resolveSetup once the fresh
  //     counts are in (independent of the page's own re-solve, which can be swallowed mid-refresh);
  //   - the page re-solved -- machinesRevision;
  //   - the save could not proceed at all (validation error, disabled_if, failed POST) -- onSaveError.
  useEffect(() => { setSavingSetupStep(false); }, [stageKey, machinesRevision, setupRevision]);

  // THE SECTION NAME AS IT READS INSIDE THE SENTENCE. A section is labelled for a tab -- "Corrugated cardboard",
  // "Carton" -- where a capital is right; the English title then drops it MID-sentence ("Select a manufacturing
  // line for Corrugated cardboard"), where it is not.
  //
  // English only, and deliberately so: the Turkish title puts the section FIRST ("Oluklu mukavva için üretim
  // hattı seçin"), so there the capital is correct and lowering it would be the error. Restricting the change to
  // English also keeps this away from the Turkish dotted/dotless i, where a naive toLowerCase turns İ into i̇.
  //
  // Only the FIRST letter, and only when the second is already lower case: that leaves an initialism like "PVC"
  // alone rather than rendering it "pVC".
  const sectionInSentence = (label) => {
    if (window.locale !== 'en' || !label) { return label; }
    if (label.length > 1 && label[1] !== label[1].toLowerCase()) { return label; }

    return label[0].toLowerCase() + label.slice(1);
  };

  const stageTitle = () => {
    if (stage.type === 'quantity') { return t('setup_wizard.quantity_title'); }
    if (stage.type === 'manufacturing_lines_source') { return t('setup_wizard.manufacturing_lines_source_title'); }
    if (stage.type === 'material') {
      return t('setup_wizard.material_title', { section: sectionInSentence(stage.label) });
    }
    // The machine steps read per machine key -- "Add a printing machine" / "Add your printing machine price" --
    // so the two die-cutting keys read right too (setup_wizard.<machineKey>_title / _price_title).
    if (stage.type === 'machine_entry') { return t(`setup_wizard.${stage.machineKey}_title`); }
    if (stage.type === MACHINE_THICKNESS_MISMATCH) { return t('setup_wizard.machine_thickness_mismatch_title'); }
    if (stage.type === 'machine_price') { return t(`setup_wizard.${stage.machineKey}_price_title`); }
    // A found line whose machine is unpriced turns the line stage into that machine's price step, so it reads with
    // the machine-price title rather than "Select a manufacturing line".
    if (lineStageMachinePrice) { return t(`setup_wizard.${lineStageMachinePrice.machineKey}_price_title`); }
    if (stage.type === 'line') {
      return t('setup_wizard.line_title', { section: sectionInSentence(stage.label) });
    }
    return t('setup_wizard.solution_title', { section: sectionInSentence(stage.label) });
  };

  const renderStageBody = () => {
    if (stage.type === 'quantity') {
      return (
        <div style={{ maxWidth: '360px', margin: '0 auto' }}>
          <QuoteBuilderForm config={quantityConfig} />
        </div>
      );
    }

    if (stage.type === 'manufacturing_lines_source') {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
          <StandardManufacturingLinesToggle value={manufacturingLinesSource} onChange={changeSource} />
        </div>
      );
    }

    // The own-lines setup steps -- add a material price, a machine, or a machine's price. Each is shown in the same
    // full-width frame (setupStepFrameStyle) and, on save, re-solves (SetupStepForms), which drops the satisfied step
    // and advances the wizard onto the next. Keyed by machinesRevision so a save that re-solves gives the next step a
    // fresh mount.
    if (['material', 'machine_entry', MACHINE_THICKNESS_MISMATCH, 'machine_price'].includes(stage.type)) {
      return (
        <div key={`wizard_setup_${stageKey}_${machinesRevision}_${setupRevision}`} {...setupStepSurfaceProps(setupStepFrameStyle)}>
          {stage.type === 'material' && (
            <MaterialPriceStep
              materialKey={stage.materialKey}
              boardThicknessMm={stage.boardThicknessMm}
              corrugatedCardboardFlute={stage.corrugatedCardboardFlute}
              submitRef={setupFormSubmitRef}
              cancelRef={materialFormCancelRef}
              onSaveError={() => setSavingSetupStep(false)}
              onFormStateChange={handleMaterialFormState}
              onResolved={resolveSetup}
            />
          )}
          {stage.type === 'machine_entry' && (
            <>
              <MachineEntryStep
                machineKey={stage.machineKey}
                allowedTypes={stage.allowedTypes}
                libraryAttributeIds={stage.libraryAttributeIds}
                onMachineAdded={resolveSetup}
              />
              {/* The owner may have machines that are only not SET for this section (a die-cutting machine not set to
                  cut its material): these say so, so they are not led to add one they already have. */}
              <SectionDisabledManufacturingLines
                navSectionIndex={navSections[sectionIndex].index}
                count={count}
                cardsPerRow={WIZARD_CARDS_PER_ROW}
                inWizard
              />
            </>
          )}
          {stage.type === MACHINE_THICKNESS_MISMATCH && (
            <MachineThicknessMismatchStep
              machineKey={stage.machineKey}
              materialThicknessMm={stage.materialThicknessMm}
              machines={stage.thicknessMismatchMachines}
              allowedTypes={stage.allowedTypes}
              libraryAttributeIds={stage.libraryAttributeIds}
              onResolved={resolveSetup}
            />
          )}
          {stage.type === 'machine_price' && stage.attributeId != null && (
            <MachinePriceStep
              machineKey={stage.machineKey}
              // The owner's unpriced machine for this section's part_type (the server's answer, refreshed after the
              // entry step, so a machine just added here is it) -- never the owner's first machine of the key, which
              // may be one this section cannot use.
              attributeId={stage.attributeId}
              submitRef={setupFormSubmitRef}
              onSaveError={() => setSavingSetupStep(false)}
              onResolved={resolveSetup}
            />
          )}
        </div>
      );
    }

    // The section's own flow, scoped to this stage. Keyed by section so moving BETWEEN sections remounts (each
    // section restores its own picked plan from the URL and its own selection from the server); moving between
    // this section's line and solution stages keeps the same instance, so the picked plan and its solved
    // solutions carry over.
    return (
      <LayoutNavSectionList
        // Source in the key too, so switching it inside the wizard remounts and re-fetches this stage.
        key={`wizard_section_${sectionIndex}_${manufacturingLinesSource}_${machinesRevision}`}
        navSections={[navSections[sectionIndex]]}
        config={config}
        count={count}
        stageView={stage.type}
        inWizard
        onWizardPickedChange={handlePicked}
        onWizardSelectionChange={handleSelection}
        onWizardMachinePriceChange={handleWizardMachinePrice}
        wizardSetupSubmitRef={setupFormSubmitRef}
        onWizardSetupResolved={resolveSetup}
        onWizardSetupSaveError={() => setSavingSetupStep(false)}
        // The owner's own lines came up empty: the standard-lines fallback offers a line to borrow, which flips the
        // quote to Standard, keeps this section on that line and lands its solution stage (see handleUseStandardLine).
        onUseStandardLine={handleUseStandardLine}
      />
    );
  };

  // Progress fills as the user advances: empty on the first stage, full on the last. It is the modal's own top
  // edge (flush inside the card's rounded corners), so it reads as part of THIS popup rather than a second
  // detached bar.
  const progressPercent = totalStages > 1 ? (stageIndex / (totalStages - 1)) * 100 : 100;

  return createPortal(
    <div style={backdropStyle}>
      <div style={modalStyle} role="dialog" aria-modal="true">
        <div style={headerStyle}>
          {/* The setup progress lives WITH the stage indicator, centred, so it is part of this one popup rather
              than a separate bar. The slim bar under the text fills as the user advances. */}
          <div style={{ gridColumn: 2, justifySelf: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
            <span style={{ color: 'var(--ink-faint, #8a94a3)', fontSize: '13px' }}>
              {t('setup_wizard.stage_indicator', { current: stageIndex + 1, total: totalStages })}
            </span>
            <div style={{ width: '160px', height: '4px', background: 'var(--surface-muted, #f1f3f5)', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progressPercent}%`, background: 'var(--brand, #eb5424)', transition: 'width 200ms ease' }} />
            </div>
          </div>
          <button type="button" onClick={closeWizard} aria-label="Close" style={{ ...closeButtonStyle, gridColumn: 3, justifySelf: 'end' }}>×</button>
        </div>

        {/* Title and the quote-wide source control share ONE row: the title stays centered (equal flex spacers)
            and the control sits at the right, the same top-right spot as the /mcc header so users find it where
            they expect. The control is offered on the stages that list manufacturing lines; the quantity stage
            lists none, so it keeps the plain centered title -- as does the source stage, whose body IS the control. */}
        {(stage.type === 'quantity' || stage.type === 'manufacturing_lines_source') ? (
          <div style={titleStyle}>{stageTitle()}</div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px', padding: '18px 20px 6px' }}>
            <div style={{ flex: 1 }} />
            <div style={{ textAlign: 'center', fontSize: '18px', fontWeight: 600, color: 'var(--ink, #10141a)' }}>
              {stageTitle()}
            </div>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <StandardManufacturingLinesToggle
                value={manufacturingLinesSource}
                onChange={changeSource}
              />
            </div>
          </div>
        )}

        <hr style={titleRuleStyle} />

        {/* The modal's one scrolling area -- see the stage reset above, which is what data-wizard-body names. */}
        <div ref={bodyRef} style={bodyStyle} data-wizard-body>{renderStageBody()}</div>

        {postError && (
          <div style={{ color: '#c00', fontSize: '13px', textAlign: 'center', padding: '0 20px 8px' }}>
            {t('setup_wizard.save_failed')}
          </div>
        )}

        <div style={footerStyle}>
          <button
            type="button"
            onClick={goBack}
            disabled={backDisabled}
            style={secondaryButtonStyle(backDisabled)}
          >
            {t('setup_wizard.back')}
          </button>
          {/* No Next on a PICKER step -- choosing a material / machine carries the wizard forward, so a Next
              would be a dead control. A price-form step keeps Next: there it SAVES the form (isPriceFormStep). */}
          {!isPickerStep && (
            <button
              type="button"
              onClick={goNext}
              disabled={!canAdvance()}
              style={primaryButtonStyle(!canAdvance())}
            >
              {(posting || savingSetupStep) ? '…' : (isLastStage ? t('setup_wizard.done') : t('setup_wizard.next'))}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const backdropStyle = {
  // Above the site's fixed top nav (its z-index would otherwise cover the modal header / stage indicator),
  // so the wizard is a true full-page modal. Centred both ways; the modal's maxHeight (90vh) plus the
  // backdrop's own overflow keeps the tall section stages from clipping when centred.
  position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(16, 24, 40, 0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4vh 2vw', overflow: 'auto',
};
const modalStyle = {
  position: 'relative', display: 'flex', flexDirection: 'column', width: '92vw', maxWidth: '1120px',
  maxHeight: '90vh', background: 'var(--surface, #ffffff)', borderRadius: '10px',
  boxShadow: '0 12px 48px rgba(16, 24, 40, 0.28)', overflow: 'hidden',
};
const headerStyle = {
  // A 1fr auto 1fr grid so the centre column (the stage indicator + progress) is truly centred on the modal,
  // regardless of the title / close-button widths -- space-between drifted it toward the narrower side.
  display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: '16px',
  padding: '14px 20px', borderBottom: '1px solid var(--rule, #e3e7ec)',
};
const closeButtonStyle = {
  background: 'none', border: 'none', fontSize: '24px', lineHeight: 1, cursor: 'pointer',
  color: 'var(--ink-faint, #8a94a3)', padding: '0 4px',
};
const titleStyle = {
  padding: '18px 20px 6px', textAlign: 'center', fontSize: '18px', fontWeight: 600,
  color: 'var(--ink, #10141a)',
};
const titleRuleStyle = {
  margin: '10px 20px 0', border: 0, borderTop: '1px solid var(--rule, #e3e7ec)',
};
const bodyStyle = { flex: 1, overflow: 'auto', padding: '10px 20px 16px' };
// A setup step takes the modal's full width, so its form uses the room the popup has rather than a narrow column
// down the middle -- and, as a setup-step surface (setupStepSurfaceProps), its dropdown column widens with it. It
// carries NO card of its own: each step's form is a QuoteBuilderForm table -- already a framed island -- and the
// machine picker frames itself, so a border here would only double up around them.
const setupStepFrameStyle = {
  width: '100%', textAlign: 'left',
};
const footerStyle = {
  display: 'flex', justifyContent: 'center', gap: '16px',
  padding: '12px 20px', borderTop: '1px solid var(--rule, #e3e7ec)',
};
const buttonBase = {
  padding: '9px 22px', fontSize: '15px', fontWeight: 600, borderRadius: '8px', cursor: 'pointer',
};
const primaryButtonStyle = (disabled) => ({
  ...buttonBase, border: 'none',
  background: disabled ? 'var(--rule, #e3e7ec)' : 'var(--brand, #eb5424)',
  color: disabled ? 'var(--ink-faint, #8a94a3)' : '#ffffff',
  cursor: disabled ? 'default' : 'pointer',
});
const secondaryButtonStyle = (disabled) => ({
  ...buttonBase, background: 'var(--surface, #ffffff)', color: 'var(--ink-soft, #4e5865)',
  border: '1px solid var(--rule, #e3e7ec)', cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.5 : 1,
});

export default FrankSetupWizard;
